import React from 'react';
import { Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { EmptyState, ListTable, Panel, SearchField, StatusChip, type ListColumn } from '../../../ui-kit';
import { fetchAccounts, watchRequests, type PsAccountCard } from '../../../services/policyServicingService';
import { matches, useLive } from '../rocco/live';
import { dayOf } from './format';
import { useAgent } from '../AgentContext';

/**
 * The employer groups the agency looks after. Each opens onto its own page:
 * its plans, its employees, what has been sent to the carriers, what they
 * billed, and every request its HR contacts have sent in.
 */
export default function AccountsPage() {
  const { agent, base } = useAgent();
  const fetcher = React.useCallback(() => fetchAccounts(agent.id), [agent.id]);
  const { data, error, loading } = useLive(fetcher, 15_000, { watch: watchRequests });
  const navigate = useNavigate();
  const [query, setQuery] = React.useState('');
  const all = data?.accounts ?? [];
  const rows = all.filter((a) => matches(query, [a.name, a.group_number, a.industry, a.location, ...a.plans]));

  const columns: ListColumn<PsAccountCard>[] = [
    {
      key: 'name',
      sortValue: (a) => a.name,
      header: 'Employer',
      grow: 1.2,
      minWidth: 200,
      render: (a) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{a.name}</Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            {[`Group ${a.group_number}`, a.industry, a.location].filter(Boolean).join(' · ')}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'employees',
      sortValue: (a) => a.employees, sortStartsDescending: true,
      header: 'Employees',
      width: 110,
      align: 'right',
      render: (a) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>
          {a.employees.toLocaleString()}
        </Typography>
      ),
    },
    {
      key: 'plans',
      header: 'Plans',
      grow: 1.3,
      minWidth: 220,
      render: (a) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{a.plans.join(' · ') || '—'}</Typography>
      ),
    },
    {
      key: 'waiting',
      sortValue: (a) => a.waiting_on_carrier, sortStartsDescending: true,
      header: 'With the carrier',
      width: 170,
      render: (a) => (a.waiting_on_carrier
        ? <StatusChip tone="warning" label={`${a.waiting_on_carrier} not heard back`} />
        : <StatusChip tone="good" label="Nothing waiting" />),
    },
    {
      key: 'requests',
      sortValue: (a) => a.open_requests, sortStartsDescending: true,
      header: 'Open requests',
      width: 130,
      align: 'right',
      render: (a) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>
          {a.open_requests.toLocaleString()}
        </Typography>
      ),
    },
    {
      key: 'renewal',
      sortValue: (a) => a.renewal_on,
      header: 'Renews',
      width: 130,
      render: (a) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{dayOf(a.renewal_on)}</Typography>
      ),
    },
  ];

  const table = (
    <ListTable
      columns={columns}
      rows={rows}
      getRowKey={(a) => a.id}
      pageSize={25}
      loading={loading}
      defaultSort={{ key: 'name', direction: 'asc' }}
      onRowClick={(a) => navigate(`${base}/accounts/${a.id}`)}
      emptyMessage={all.length
        ? 'Nothing matches. Clear the search.'
        : 'No employer accounts yet. An employer appears here once its group is set up for this agent.'}
    />
  );
  const searchBox = <SearchField value={query} onChange={setQuery} placeholder="Search accounts…" />;

  return (
    <Panel
      title="Accounts"
      subtitle={`${all.length} employer group${all.length === 1 ? '' : 's'} · open one for its plans, employees and requests`}
      search={searchBox}
    >
      {error && !data ? <EmptyState message={error} /> : table}
    </Panel>
  );
}
